import { useEffect, useRef } from 'react';
import AMapLoader from '@amap/amap-jsapi-loader';
import footprintsData from '../data/redFootprints.json';
import './MapContainer.css';

// 声明高德地图类型
declare global {
  interface Window {
    AMap: any;
    _AMapSecurityConfig: {
      securityJsCode?: string;
    };
  }
}

interface MapContainerProps {
  onMapReady?: (map: any) => void;
}

export default function MapContainer({ onMapReady }: MapContainerProps) {
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      try {
        // 安全密钥配置
        window._AMapSecurityConfig = {
          securityJsCode: 'e44c3865391b3a3452ae22d64176a822', // 高德地图安全密钥
        };

        const AMap = await AMapLoader.load({
          key: 'fc33e00a5cdc3d3d2a50595a071276ad', // 高德地图 API Key
          version: '2.0', // 指定要加载的 JSAPI 的版本
          plugins: ['AMap.Scale', 'AMap.ToolBar'], // 需要使用的插件列表
        });

        if (!isMounted || !mapContainerRef.current) return;

        // 创建地图实例
        const map = new AMap.Map('map-container', {
          viewMode: '2D', // 2D地图模式
          zoom: 11, // 初始化地图级别
          center: [109.1208, 21.4733], // 北海市中心坐标
          mapStyle: 'amap://styles/normal',
        });

        // 创建红色标记图标
        const svgIcon = encodeURIComponent(`
          <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 0C7.163 0 0 7.163 0 16c0 11.046 16 24 16 24s16-12.954 16-24C32 7.163 24.837 0 16 0z" 
                  fill="#C8102E" stroke="#FFD700" stroke-width="2"/>
            <circle cx="16" cy="16" r="6" fill="#FFFFFF"/>
          </svg>
        `);
        const redIcon = new AMap.Icon({
          size: new AMap.Size(32, 40),
          image: 'data:image/svg+xml,' + svgIcon,
          imageOffset: new AMap.Pixel(0, 0),
          imageSize: new AMap.Size(32, 40),
        });

        // 添加标记点
        (footprintsData as any[]).forEach((footprint: any) => {
          try {
            const marker = new AMap.Marker({
              position: [footprint.lng, footprint.lat],
              title: footprint.name,
              map: map,
              icon: redIcon,
            });

            // 创建信息窗口
            const imagePath = `/images/footprints/${footprint.id}.png`;
            const infoWindow = new AMap.InfoWindow({
              content: `
                <div style="padding: 10px; min-width: 250px; max-width: 400px;">
                  <h3 style="margin: 0 0 10px 0; color: #C8102E; font-size: 16px; font-weight: 700;">${footprint.name}</h3>
                  <img src="${imagePath}" 
                       alt="${footprint.name}" 
                       style="width: 100%; max-width: 350px; height: auto; border-radius: 8px; margin-bottom: 10px; display: block; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"
                       onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
                  <div style="display: none; padding: 20px; text-align: center; color: #999; font-size: 12px;">
                    图片加载失败
                  </div>
                  <p style="margin: 8px 0 4px 0; color: #666; font-size: 14px; font-weight: 500;">📍 ${footprint.address}</p>
                  <p style="margin: 8px 0 0 0; color: #333; font-size: 13px; line-height: 1.6;">${footprint.description}</p>
                </div>
              `,
              offset: new AMap.Pixel(0, -30),
            });

            // 点击标记显示信息窗口
            marker.on('click', () => {
              infoWindow.open(map, marker.getPosition());
            });
          } catch (markerError) {
            console.error(`标记点 ${footprint.name} 创建失败:`, markerError);
          }
        });

        mapRef.current = map;

        // 调用回调函数，通知地图已准备好
        if (onMapReady) {
          onMapReady(map);
        }
      } catch (e: any) {
        console.error('地图加载失败:', e);
        // 显示错误信息
        if (isMounted && mapContainerRef.current) {
          const errorMessage = e?.message || e?.toString() || '未知错误';
          mapContainerRef.current.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666; text-align: center; padding: 20px;">
              <div>
                <p style="margin: 0 0 10px 0; font-size: 16px; color: #C8102E; font-weight: 700;">地图加载失败</p>
                <p style="margin: 4px 0; font-size: 13px; color: #999;">${errorMessage}</p>
                <p style="margin: 8px 0 0 0; font-size: 14px;">请检查网络连接或高德地图 API Key 配置</p>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: #999;">如果出现 USERKEY_PLAT_NOMATCH 错误，请确保 Key 类型为「Web 端（JS API）」</p>
                <p style="margin: 8px 0 0 0; font-size: 12px; color: #999;">详见 AMAP_SETUP.md</p>
              </div>
            </div>
          `;
        }
      }
    };

    initMap();

    // 清理函数
    return () => {
      isMounted = false;
      if (mapRef.current) {
        try {
          mapRef.current.destroy();
        } catch (error) {
          console.error('地图销毁失败:', error);
        }
        mapRef.current = null;
      }
    };
  }, [onMapReady]);

  return (
    <div
      id="map-container"
      ref={mapContainerRef}
      className="map-container"
    ></div>
  );
}

